import { useState, type FormEvent } from 'react'
import { toast } from 'react-toastify'
import { Button } from '../../components/button'
import { TextInput } from '../../components/inputs/textInput'
import { getInterestErrorMessage } from '../../redux-toolkits/interests/interests.helpers'
import {
  useGetDiscoverImpressionsQuery,
  useGetFeedWeightsQuery,
  useSaveFeedWeightsMutation,
  type FeedWeights,
} from '../../redux-toolkits/discover/discover-slice'

const labels: [keyof FeedWeights, string][] = [
  ['followedCreator', 'Creators followed'],
  ['creatorAffinity', 'Creator affinity'],
  ['category', 'Category match'],
  ['tag', 'Tag match'],
  ['trending', 'Trending products'],
  ['freshness', 'New products'],
  ['spending', 'Spending preference'],
]
function WeightsForm({
  initial,
  defaults,
}: {
  initial: FeedWeights
  defaults: FeedWeights
}) {
  const [weights, setWeights] = useState(initial)
  const total =
    Math.round(labels.reduce((sum, [key]) => sum + weights[key], 0) * 100) / 100
  const remaining = Math.round((100 - total) * 100) / 100
  const validAllocation =
    Math.abs(labels.reduce((sum, [key]) => sum + weights[key], 0) - 100) <
      0.000001 &&
    labels.every(
      ([key]) =>
        Number.isFinite(weights[key]) &&
        weights[key] >= 0 &&
        weights[key] <= 100,
    )
  const [saveWeights, { isLoading }] = useSaveFeedWeightsMutation()
  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!validAllocation) {
      toast.error('Ranking percentages must add up to 100%.')
      return
    }
    try {
      await saveWeights(weights).unwrap()
      toast.success('Ranking saved. New visits will use this allocation.')
    } catch (error) {
      toast.error(getInterestErrorMessage(error))
    }
  }
  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        void save(event)
      }}
    >
      <p className="text-sm text-pry-gray">
        Share 100% across the ranking signals. A larger share gives a signal
        more influence; 0% disables it. Changes apply to new visits.
      </p>
      <div
        className={`rounded-xl border p-4 ${
          validAllocation
            ? 'border-green-600 bg-green-50'
            : 'border-amber-500 bg-amber-50'
        }`}
        aria-live="polite"
      >
        <p className="font-semibold">{total}% of 100% allocated</p>
        <p className="mt-1 text-sm">
          {validAllocation
            ? 'Your ranking allocation is ready to save.'
            : remaining > 0
              ? `Allocate the remaining ${remaining}%.`
              : `Reduce your allocation by ${Math.abs(remaining)}%.`}
        </p>
        <div
          className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200"
          aria-hidden="true"
        >
          <div
            className={`h-full ${
              validAllocation ? 'bg-green-600' : 'bg-amber-500'
            }`}
            style={{ width: `${Math.min(100, Math.max(0, total))}%` }}
          />
        </div>
      </div>
      <fieldset disabled={isLoading} className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          {labels.map(([key, label]) => (
            <TextInput
              key={key}
              title={`${label} (%)`}
              hasIcon={false}
              required
              type="number"
              min={0}
              max={100}
              step={0.01}
              value={String(weights[key])}
              extra={<span className="text-sec-black">%</span>}
              onChange={(event) =>
                setWeights({ ...weights, [key]: Number(event.target.value) })
              }
            />
          ))}
        </div>
        <div className="flex gap-3">
          <Button
            label={isLoading ? 'Saving…' : 'Save ranking'}
            primary
            type="submit"
            disabled={!validAllocation || isLoading}
          />
          <Button
            label="Restore defaults"
            type="button"
            onClick={() => setWeights(defaults)}
          />
        </div>
        <p className="text-sm text-pry-gray">
          Restoring defaults fills the form. Save to apply them.
        </p>
      </fieldset>
    </form>
  )
}
export function FeedWeightsSettings() {
  const { data, isLoading, isError, refetch } = useGetFeedWeightsQuery()
  if (isLoading) return <p>Loading ranking settings…</p>
  if (isError || data === undefined)
    return (
      <p role="alert">
        Could not load ranking settings.{' '}
        <button
          type="button"
          onClick={() => {
            void refetch()
          }}
        >
          Retry
        </button>
      </p>
    )
  return (
    <WeightsForm
      key={JSON.stringify(data.weights)}
      initial={data.weights}
      defaults={data.defaults}
    />
  )
}
export function ImpressionReport() {
  const [from, setFrom] = useState(() =>
    new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10),
  )
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10))
  const validRange =
    from !== '' &&
    to !== '' &&
    from <= to &&
    new Date(to).getTime() - new Date(from).getTime() < 90 * 86400000
  const { data, isFetching, isError, refetch } = useGetDiscoverImpressionsQuery(
    {
      from: `${from}T00:00:00.000Z`,
      to:
        to === ''
          ? ''
          : new Date(
              new Date(`${to}T00:00:00.000Z`).getTime() + 86400000,
            ).toISOString(),
    },
    { skip: !validRange },
  )
  return (
    <section className="space-y-4 border-t pt-6">
      <h2 className="text-xl font-semibold">Feed impressions</h2>
      <p className="text-sm text-pry-gray">
        Cards reported as viewed by signed-in customers. Reports update after
        processing. Dates use UTC; the last 90 days are retained.
      </p>
      <div className="flex flex-wrap gap-4">
        <TextInput
          title="From"
          hasIcon={false}
          type="date"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
        />
        <TextInput
          title="Through"
          hasIcon={false}
          type="date"
          value={to}
          onChange={(event) => setTo(event.target.value)}
        />
        <Button
          label="Refresh"
          type="button"
          disabled={!validRange || isFetching}
          onClick={() => {
            void refetch()
          }}
        />
      </div>
      {!validRange && (
        <p role="alert">Choose an increasing range of up to 90 days.</p>
      )}
      {isFetching && <p>Loading report…</p>}
      {isError && <p role="alert">Could not load the impression report.</p>}
      {validRange && data !== undefined && !isError && (
        <>
          <p>
            <strong>{data.totals.impressions.toLocaleString()}</strong>{' '}
            impressions ·{' '}
            <strong>{data.totals.viewers.toLocaleString()}</strong> unique
            viewers
          </p>
          {data.totals.impressions === 0 ? (
            <p>No impressions recorded for these dates.</p>
          ) : (
            <>
              <table className="w-full text-left">
                <caption className="text-left font-medium py-3">
                  Daily impressions
                </caption>
                <thead>
                  <tr>
                    <th>Date (UTC)</th>
                    <th>Impressions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily.map((day) => (
                    <tr key={day.day}>
                      <td className="py-2">{day.day}</td>
                      <td>{day.impressions.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <caption className="text-left font-medium py-3">
                    Most viewed cards
                  </caption>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Content ID</th>
                      <th>Impressions</th>
                      <th>Viewers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cards.map((card) => (
                      <tr key={`${card.type}:${card.id}`}>
                        <td className="py-2">
                          {card.type.replaceAll('_', ' ')}
                        </td>
                        <td>{card.id}</td>
                        <td>{card.impressions.toLocaleString()}</td>
                        <td>{card.viewers.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}
